import React, { useState, useEffect, useRef } from 'react';
import { Container, Table, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import api, { getInventoryByLot } from '../services/api';
import useDebounce from '../hooks/useDebounce';

function Catalog() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const observerTarget = useRef(null);
  const { user } = useAuth();

  // Fetch catalog data
  const fetchCatalog = async (page = 1, reset = false) => {
    try {
      console.log('Fetching catalog with user:', user);
      setLoading(true);
      setError(null);
      
      // Check if we have the required user data
      if (!user?.customerId) {
        console.log('Missing user data:', user);
        setError('User data not available. Please try logging in again.');
        setLoading(false);
        return;
      }

      const response = await getInventoryByLot(page, 50, debouncedSearchTerm);
      console.log('Catalog response:', response);
      
      const items = response?.items || [];
      const pagination = response?.pagination || { total: 0, totalPages: 1 };
      
      // Group the lots by SKU and calculate totals
      const groupedData = items.reduce((acc, lot) => {
        const key = lot.itemNumber;
        if (!acc[key]) {
          acc[key] = {
            itemNumber: lot.itemNumber,
            itemName: lot.itemName,
            onHand: 0,
            availQty: 0,
            tap: 0,
            lots: []
          };
        }
        // Use 0 as default value if quantities are null or undefined
        const onHandQty = Number(lot.onHand || 0);
        const availableQty = Number(lot.availQty || 0);
        const ppuValue = Number(lot.ppu || 1);

        acc[key].onHand += onHandQty;
        acc[key].availQty += availableQty;
        acc[key].tap += availableQty * ppuValue;
        acc[key].lots.push(lot);
        return acc;
      }, {});

      const newData = Object.values(groupedData);
      
      if (reset) {
        setCatalog(newData);
      } else {
        setCatalog(prev => [...prev, ...newData]);
      }
      
      setTotalItems(pagination.total);
      setHasMore(page < pagination.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching catalog:', err);
      setError('Failed to load catalog data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Catalog component mounted, user:', user);
    if (user?.customerId) {
      fetchCatalog(1, true);
    } else {
      setLoading(false);
      if (user) {
        setError('User ID not available. Please try logging in again.');
      }
    }
  }, [user?.customerId]);

  // Handle search with debounce
  useEffect(() => {
    if (user && debouncedSearchTerm !== undefined) {
      setCurrentPage(1);
      fetchCatalog(1, true);
    }
  }, [debouncedSearchTerm, user]);

  // Setup infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchCatalog(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, currentPage]);

  // Handle search
  const filteredCatalog = catalog;

  // Export to Excel
  const handleExport = () => {
    const exportData = filteredCatalog.flatMap(item => 
      item.lots.map(lot => ({
        'Item Number': lot.itemNumber,
        'Item Name': lot.itemName,
        'Lot ID': lot.idLevels,
        'PPU': lot.ppu || 1,
        'Units': lot.onHand,
        'Available': lot.availQty,
        'TAP': (lot.availQty || 0) * (lot.ppu || 1),
        'Notes': lot.notes || ''
      }))
    );

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Catalog');
    XLSX.writeFile(wb, 'catalog_export.xlsx');
  };

  if (loading && catalog.length === 0) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4 align-items-center">
        <Col md={3}>
          <h2 className="mb-0">Catalog Management</h2>
        </Col>
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search catalog..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={3} className="text-end">
          <Button
            variant="success"
            onClick={handleExport}
            className="me-2"
          >
            <FontAwesomeIcon icon={faFileExcel} className="me-2" />
            Export to Excel
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <Table striped bordered hover className="mb-0">
          <thead>
            <tr>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>Item Number</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>Item Name</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end">TAP</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end">Units</th>
              <th style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }} className="text-end">Available</th>
            </tr>
          </thead>
          <tbody>
            {filteredCatalog.map(item => (
              <tr key={item.itemNumber}>
                <td>{item.itemNumber}</td>
                <td>{item.itemName}</td>
                <td className="text-end">{item.tap.toLocaleString()}</td>
                <td className="text-end">{item.onHand.toLocaleString()}</td>
                <td className="text-end">{item.availQty.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {/* Infinite scroll trigger */}
        <div 
          ref={observerTarget}
          style={{ height: '20px', width: '100%' }}
        >
          {loading && (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}

export default Catalog; 